import { useEffect, useRef, useState } from "react";
import { CheckCircle, Circle, LockSimple, PencilSimple, Trash, PlayCircle, BookOpen, Signature, FileVideo, WarningCircle, GraduationCap } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import ListenButton from "../components/ListenButton";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SignaturePad } from "../components/SignaturePad";
import { LiveSessionsAdmin, MyLiveSessions } from "../components/LiveSessionsPanel";
import { DesignatedCaregiverCard } from "../components/DesignatedCaregiverCard";

const KIND_LABELS = { curriculum: "Orientation part", reference: "Reference", acknowledgment: "Final signature" };

const videoEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

const MOD_STATUS = {
  complete: ["Complete", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  in_progress: ["In progress", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  remediation_due: ["Rewatch assigned", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  remediation_overdue: ["Rewatch overdue", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
  waiting_for_content: ["Videos coming", "border-stone-300 bg-stone-100 text-stone-500"],
  locked: ["Locked", "border-stone-200 bg-stone-50 text-stone-400"],
  not_started: ["Not started", "border-stone-300 bg-stone-100 text-stone-600"],
};

const SECTION_IMAGES = [
  [/^Part I\b/, "/shc-door-greet.webp"],
  [/^Employee Handbook$/, "/orient-handbook.webp"],
  [/^Part II\b/, "/shc-companionship-2.webp"],
  [/^Your Role/, "/brand-personal-care.webp"],
  [/^Dress Code/, "/shc-door.webp"],
  [/^Part III\b/, "/shc-wiping.webp"],
  [/^If You Are Hurt/, "/shc-gen-steps.webp"],
  [/^Medicaid Fraud/, "/shc-evv-phone.webp"],
  [/^Driving Clients/, "/brand-transport.webp"],
  [/^A Harassment-Free/, "/purple-comfort.webp"],
  [/^Who to Call/, "/scene-videocall.webp"],
  [/^Part IV\b/, "/shc-china-cabinet.webp"],
  [/^Part V\b/, "/shc-evv-phone.webp"],
  [/^Part VI\b/, "/shc-gen-training-room.webp"],
  [/^Part VII\b/, "/scene-manicure.webp"],
  [/^Part VIII\b/, "/shc-gen-family-welcome.webp"],
  [/^Competency Exam$/, "/orient-exam.webp"],
];

const videoEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (yt) return { type: "iframe", src: `https://www.youtube-nocookie.com/embed/${yt[1]}` };
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { type: "video", src: url };
};

const formatBlocks = (text) => {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    const isBullet = /^[-•]\s+/.test(line);
    const isNum = /^\d+\.\s+/.test(line);
    const isCapsHead = !isBullet && !isNum && line.length > 3 && line.length < 90 && /[A-Z]{4,}/.test(line) && line === line.toUpperCase();
    const isColonHead = !isBullet && !isNum && line.length > 3 && line.length < 90 && /^[A-Z][A-Za-z0-9 &,'()—/-]+:$/.test(line);
    if (isBullet || isNum) {
      const type = isNum ? "ol" : "ul";
      if (!list || list.type !== type) { flush(); list = { type, items: [] }; }
      list.items.push(line.replace(/^[-•]\s+|^\d+\.\s+/, ""));
      continue;
    }
    flush();
    if (isCapsHead || isColonHead) blocks.push({ type: "h", text: line.replace(/:$/, "") });
    else blocks.push({ type: "p", text: line });
  }
  flush();
  return blocks;
};

function OrientationBody({ text, small = false }) {
  const body = small ? "text-sm" : "text-base";
  return (
    <div className="space-y-3">
      {formatBlocks(text).map((b, i) => b.type === "h" ? (
        <p key={i} className={`pt-3 font-heading ${small ? "text-sm" : "text-base"} font-bold uppercase tracking-wide text-stone-900`}>{b.text}</p>
      ) : b.type === "p" ? (
        <p key={i} className={`${body} leading-relaxed text-stone-700`}>{b.text}</p>
      ) : b.type === "ul" ? (
        <ul key={i} className={`list-disc space-y-1.5 pl-5 ${body} leading-relaxed text-stone-700`}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>
      ) : (
        <ol key={i} className={`list-decimal space-y-1.5 pl-5 ${body} leading-relaxed text-stone-700`}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</ol>
      ))}
    </div>
  );
}

export default function OrientationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [data, setData] = useState(null);
  const [reading, setReading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState(null);
  const [forms, setForms] = useState([]);
  const [formStep, setFormStep] = useState(null);
  const [formResp, setFormResp] = useState({});
  const [formSig, setFormSig] = useState(null);
  const [activeSec, setActiveSec] = useState("");
  const secRefs = useRef({});
  const [obData, setObData] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [examInfo, setExamInfo] = useState({});
  const [examAnswers, setExamAnswers] = useState({});
  const [examSig, setExamSig] = useState(null);
  const [examResult, setExamResult] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [dcFile, setDcFile] = useState(null);

  const loadDcFile = () => user && api.get(`/employees/${user.id}/file`).then((r) => setDcFile(r.data)).catch(() => setDcFile(false));

  const load = () => api.get("/orientation").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/screenings").then((r) => setForms(Array.isArray(r.data) ? r.data : (r.data?.forms || []))).catch(() => {}); }, []);
  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setStaffList((r.data.users || r.data || []).filter((u) => u.active !== false && u.role !== "admin"))).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!data) return;
    const obs = new IntersectionObserver((entries) => {
      for (const en of entries) if (en.isIntersecting) setActiveSec(en.target.id);
    }, { rootMargin: "-20% 0px -70% 0px" });
    Object.values(secRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (!isAdmin) {
      api.get("/onboarding/modules").then((r) => setObData(r.data)).catch(() => {});
      if (data.steps.some((s) => s.seed_key === "designated-caregiver-step")) loadDcFile();
    }
    for (const s of data.steps.filter((x) => x.exam_topic_id)) {
      api.get(`/orientation/exam/${s.id}`).then((r) => setExamInfo((m) => ({ ...m, [s.id]: r.data }))).catch(() => {});
    }
  }, [data]);

  const scrollTo = (id) => {
    const el = secRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleResp = (fid, opt) => {
    setFormResp((r) => {
      const cur = new Set(r[fid] || []);
      if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
      return { ...r, [fid]: Array.from(cur) };
    });
  };

  const submitFormStep = async () => {
    if (!formSig) { toast.error("Please sign before submitting"); return; }
    setBusy(true);
    try {
      await api.post(`/screenings/${formStep.screening_form_id}/submit`, { signature_png: formSig, responses: formResp });
      await api.post(`/orientation/steps/${formStep.id}/complete`);
      toast.success("Screening signed and filed on your record");
      setFormStep(null); setFormResp({}); setFormSig(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markDone = async (step) => {
    setBusy(true);
    try {
      await api.post(`/orientation/steps/${step.id}/complete`);
      toast.success(`"${step.title}" marked complete`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const signAck = async () => {
    if (!sig) { toast.error("Please draw your signature"); return; }
    setBusy(true);
    try {
      await api.post("/orientation/acknowledge", { signature_png: sig });
      toast.success("Orientation complete — acknowledgment signed and filed");
      setSig(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const signVideo = async (m, v) => {
    const signature = signatures[v.id];
    if (!signature) { toast.error("Draw your signature first"); return; }
    setBusy(true);
    try {
      const { data: result } = await api.post(`/onboarding/modules/${m.id}/videos/${v.id}/sign`, { signature_png: signature });
      if (result.remediation_completed) toast.success("Remedial rewatch completed");
      else if (result.module_completed) toast.success(`"${m.title}" complete`);
      else toast.success("Video signed");
      setSignatures((s) => ({ ...s, [v.id]: null }));
      api.get("/onboarding/modules").then((r) => setObData(r.data)).catch(() => {});
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitExam = async (step) => {
    const info = examInfo[step.id];
    const answers = examAnswers[step.id] || {};
    if ((info?.questions || []).some((q) => answers[q.id] == null)) { toast.error("Answer every question first"); return; }
    if (!examSig) { toast.error("Please draw your signature — it attests this work is your own"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/orientation/exam/${step.id}/submit`, { answers, signature_png: examSig });
      setExamResult({ sid: step.id, ...r });
      if (r.passed) toast.success(`Exam passed — ${r.score}%. Certificate filed on your record.`);
      else toast.error(`Score ${r.score}% — passing is ${r.passing_score}%${r.attempts_left != null ? ` · ${r.attempts_left} attempt(s) left` : ""}`);
      setExamSig(null);
      setExamAnswers((a) => ({ ...a, [step.id]: {} }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (s) => setEditing({ id: s.id, title: s.title, content: s.content, kind: s.kind, order: s.order, version: s.version, screening_form_id: s.screening_form_id || "", video_url: s.video_url || "" });

  const saveStep = async () => {
    if (!editing.title.trim() || !editing.content.trim()) { toast.error("Title and content are required"); return; }
    setBusy(true);
    try {
      const payload = { title: editing.title.trim(), content: editing.content.trim(), kind: editing.kind, order: Number(editing.order) || 50, screening_form_id: editing.screening_form_id || "", video_url: (editing.video_url || "").trim() };
      if (editing.id) {
        const { data: r } = await api.put(`/orientation/steps/${editing.id}`, payload);
        toast.success(r.version > editing.version ? `Saved as version ${r.version}` : "Saved");
      } else {
        await api.post("/orientation/steps", payload);
        toast.success("Step added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeStep = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? Staff progress on it is kept in history but the step disappears.`)) return;
    try {
      await api.delete(`/orientation/steps/${s.id}`);
      toast.success("Step deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const curriculum = data.steps.filter((s) => s.kind === "curriculum");
  const ackStep = data.steps.find((s) => s.kind === "acknowledgment");
  const refs = data.steps.filter((s) => s.kind === "reference");
  const doneCount = curriculum.filter((s) => s.done_at).length;
  const allDone = curriculum.length > 0 && doneCount === curriculum.length;
  const pct = curriculum.length ? Math.round((doneCount / curriculum.length) * 100) : 0;

  const obModules = !isAdmin ? (obData?.modules || []) : [];
  const obSummary = obData?.summary || {};
  const tocItems = [
    ...obModules.map((m) => ({ key: `sec-mod-${m.id}`, label: `Video: ${m.title}`, done: m.status === "complete" })),
    ...curriculum.map((s) => ({ key: `sec-${s.id}`, label: s.title, done: !!s.done_at })),
    ...(ackStep ? [{ key: "sec-ack", label: ackStep.title, done: !!data.acknowledged }] : []),
    ...(refs.length ? [{ key: "sec-refs", label: "Reference library", done: false }] : []),
  ];

  return (
    <div className="max-w-7xl" data-testid="orientation-page">
      {/* Hero — editorial band in the client-handbook style */}
      <div className="relative mb-10 overflow-hidden rounded-2xl" data-testid="orientation-banner">
        <img src="/brand-orientation.webp" alt="" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/95 via-stone-900/80 to-stone-900/40" />
        <div className="relative px-6 py-12 sm:px-10 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Silver Home Care</p>
          <h1 className="mt-2 font-heading text-4xl font-semibold tracking-tight text-white sm:text-5xl">Your Orientation</h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-white/85">
            Welcome. Read each part at your own pace and check it off, then sign the final acknowledgment.
            Orientation must be completed before you can be assigned to clients.
          </p>
          <div className="mt-5 inline-flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
            <span className="text-xs font-medium text-white/80">{data.acknowledged ? "Orientation complete" : `${doneCount} of ${curriculum.length} parts completed`}</span>
            <span className="block h-1.5 w-28 overflow-hidden rounded-full bg-white/20">
              <span className="block h-full rounded-full bg-white transition-all" style={{ width: `${data.acknowledged ? 100 : pct}%` }} />
            </span>
          </div>
        </div>
      </div>

      {isAdmin ? <LiveSessionsAdmin staff={staffList} /> : <MyLiveSessions />}

      <div className="grid grid-cols-12 items-start gap-10">
        {/* Sticky TOC */}        <aside className="col-span-12 lg:col-span-3 lg:sticky lg:top-24">
          <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="orientation-progress">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-400">Contents</p>
            <nav className="mt-3 space-y-0.5" data-testid="orientation-steps">
              {tocItems.map((it) => (
                <button key={it.key} onClick={() => scrollTo(it.key)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] transition-all hover:translate-x-0.5 ${activeSec === it.key ? "bg-stone-100 font-semibold text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
                  data-testid={`orientation-toc-${it.key}`}>
                  {it.done ? <CheckCircle size={15} weight="fill" className="shrink-0 text-[#27AE60]" /> : <Circle size={15} className="shrink-0 text-stone-300" />}
                  <span className="min-w-0 truncate">{it.label}</span>
                </button>
              ))}
            </nav>
            <div className="mt-4 border-t border-stone-100 pt-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-stone-700">{data.acknowledged ? "Complete ✓" : `${pct}% complete`}</p>
                {isAdmin && data.staff_total != null && (
                  <p className="text-[11px] text-stone-400" data-testid="orientation-staff-stats">{data.staff_completed}/{data.staff_total} staff oriented</p>
                )}
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-stone-100">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${data.acknowledged ? 100 : pct}%` }} data-testid="orientation-progress-bar" />
              </div>
            </div>
            {isAdmin && (
              <Button onClick={() => setEditing({ title: "", content: "", kind: "curriculum", order: 50, version: 1, screening_form_id: "", video_url: "" })}
                size="sm" className="mt-4 w-full rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="orientation-add-step">
                + Add Step
              </Button>
            )}
          </div>
        </aside>

        {/* Editorial sections */}
        <main className="col-span-12 space-y-12 lg:col-span-9">
          {obModules.length > 0 && (
            <>
              <div data-testid="orientation-video-intro">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#82778A]">Step one — video training</p>
                <h2 className="mt-1.5 font-heading text-2xl font-semibold tracking-tight text-stone-900 sm:text-3xl">Watch & sign each training video</h2>
                <p className="mt-1 max-w-prose text-sm text-stone-500">Your signature on each video is your proof of training. Topics unlock in order — finish one to open the next.</p>
              </div>
              {(obSummary.remediations || []).length > 0 && (
                <div className="rounded-2xl border border-[#B7791F]/30 bg-[#B7791F]/5 p-5" data-testid="remediation-alert">
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#8a5d14]"><WarningCircle size={18} /> Rewatch assigned after your competency exam</p>
                  <div className="mt-3 space-y-2">
                    {obSummary.remediations.map((r) => (
                      <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#B7791F]/20 bg-white p-3" data-testid={`remediation-${r.id}`}>
                        <div>
                          <p className="text-sm font-medium text-stone-900">{r.title}</p>
                          <p className="text-xs text-stone-500">{r.wrong_count} missed · due {String(r.due_at || "").slice(0, 10)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-stone-600">Re-watch the assigned topic below and sign each video again.</p>
                </div>
              )}
              {obModules.map((m, idx) => {
                const [modLabel, modCls] = MOD_STATUS[m.status] || MOD_STATUS.not_started;
                const needsRemedial = !!m.active_remediation;
                return (
                  <section key={m.id} id={`sec-mod-${m.id}`} ref={(el) => { secRefs.current[`sec-mod-${m.id}`] = el; }}
                    className={`scroll-mt-24 border-b border-stone-200/70 pb-12 ${m.unlocked ? "" : "opacity-70"}`} data-testid={`orientation-module-${m.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-primary">{idx + 1}</span>
                      <h2 className="font-heading text-2xl font-semibold tracking-tight text-stone-900">{m.title}</h2>
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${modCls}`} data-testid={`module-status-${m.id}`}>{modLabel}</span>
                      {!m.unlocked && <LockSimple size={18} className="text-stone-400" />}
                    </div>
                    {m.description && <p className="mt-1 max-w-prose text-sm text-stone-500">{m.description}</p>}
                    <div className="mt-4 space-y-4">
                      {(m.videos || []).length === 0 && (
                        <p className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-4 text-sm text-stone-400" data-testid={`module-no-videos-${m.id}`}>Videos for this topic are being uploaded by the office.</p>
                      )}
                      {(m.videos || []).map((v, vi) => {
                        const signed = !!v.signed_at;
                        const remedialSigned = !!v.remediation_signed_at;
                        const needsSignature = m.unlocked && (!signed || (needsRemedial && !remedialSigned));
                        return (
                          <div key={v.id} className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`orientation-video-${v.id}`}>
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="flex items-center gap-2 text-sm font-medium text-stone-900"><FileVideo size={18} className="text-primary" /> Video {vi + 1}: {v.title}</p>
                              {needsRemedial && !remedialSigned ? (
                                <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-xs font-medium text-[#8a5d14]">Remedial rewatch required</span>
                              ) : signed ? (
                                <span className="inline-flex items-center gap-1 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-xs font-medium text-[#1e8449]"><CheckCircle size={13} weight="fill" /> Signed {String(v.signed_at).slice(0, 10)}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500"><Circle size={13} /> Not signed</span>
                              )}
                            </div>
                            {v.kind === "link" ? (
                              videoEmbedUrl(v.url) ? (
                                <iframe src={videoEmbedUrl(v.url)} title={v.title} className="aspect-video w-full rounded-lg border border-stone-200 bg-black" allowFullScreen data-testid={`video-player-${v.id}`} />
                              ) : (
                                <a href={v.url} target="_blank" rel="noreferrer" className="block rounded-lg border border-stone-200 bg-accent/30 p-4 text-sm font-medium text-primary hover:underline" data-testid={`video-player-${v.id}`}>Open training video: {v.title}</a>
                              )
                            ) : (
                              <video controls preload="metadata" className="aspect-video w-full rounded-lg border border-stone-200 bg-black" src={`${api.defaults.baseURL}/onboarding/videos/${v.id}`} data-testid={`video-player-${v.id}`} />
                            )}
                            {needsSignature && (
                              <div className="mt-4 rounded-lg border border-stone-200 bg-accent/30 p-4">
                                <p className="text-sm text-stone-700">I attest that I watched and understand this training video.</p>
                                <div className="mt-3"><SignaturePad onChange={(sig) => setSignatures((s) => ({ ...s, [v.id]: sig }))} /></div>
                                <div className="mt-3 flex justify-end">
                                  <Button onClick={() => signVideo(m, v)} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`sign-video-${v.id}`}>
                                    {needsRemedial ? "Sign remedial rewatch" : "Sign video"}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
              <div data-testid="orientation-reading-intro">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#82778A]">Step two — orientation reading</p>
                <h2 className="mt-1.5 font-heading text-2xl font-semibold tracking-tight text-stone-900 sm:text-3xl">Read each part and check it off</h2>
              </div>
            </>
          )}
          {curriculum.map((s, i) => {
            const vid = videoEmbed(s.video_url);
            const sImg = SECTION_IMAGES.find(([re]) => re.test(s.title))?.[1];
            return (
              <section key={s.id} id={`sec-${s.id}`} ref={(el) => { secRefs.current[`sec-${s.id}`] = el; }}
                className="scroll-mt-24 border-b border-stone-200/70 pb-12 flow-root" data-testid={`orientation-step-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#82778A]">Part {i + 1} of {curriculum.length}</p>
                    <h2 className="mt-1.5 font-heading text-2xl font-semibold leading-snug tracking-tight text-stone-900 sm:text-3xl">{s.title}</h2>
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 items-center gap-1 pt-1">
                      <button onClick={() => openEdit(s)} className="rounded-full p-1.5 text-stone-300 hover:bg-stone-100 hover:text-stone-700" data-testid={`orientation-edit-${s.id}`}><PencilSimple size={16} /></button>
                      <button onClick={() => removeStep(s)} className="rounded-full p-1.5 text-stone-300 hover:bg-stone-100 hover:text-[#C0392B]" data-testid={`orientation-delete-${s.id}`}><Trash size={16} /></button>
                    </div>
                  )}
                </div>

                {vid && (
                  <div className="mt-5 overflow-hidden rounded-2xl border border-stone-200 bg-stone-900 shadow-sm" data-testid={`orientation-video-${s.id}`}>
                    {vid.type === "iframe" ? (
                      <div className="aspect-video w-full">
                        <iframe src={vid.src} title={`${s.title} video`} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      </div>
                    ) : (
                      <video src={vid.src} controls className="aspect-video w-full" />
                    )}
                    <p className="flex items-center gap-1.5 bg-white px-4 py-2 text-[11px] font-medium text-stone-500"><PlayCircle size={14} className="text-primary" /> Watch before checking off this part</p>
                  </div>
                )}

                <div className="mt-5 max-w-prose flow-root" data-testid={`orientation-content-${s.id}`}>
                  <div className="mb-2"><ListenButton text={`${s.title}. ${s.content}`} label="Listen to this section" testid={`orientation-listen-${s.id}`} /></div>
                  {sImg && (
                    <img src={sImg} alt="" loading="lazy"
                      className={`mb-4 w-full rounded-2xl border border-stone-200 shadow-sm sm:w-2/5 ${i % 2 ? "sm:float-left sm:mr-6" : "sm:float-right sm:ml-6"}`}
                      data-testid={`orientation-image-${s.id}`} />
                  )}
                  <OrientationBody text={s.content} />
                </div>

                {s.exam_topic_id ? (
                  <div className="mt-6 clear-both" data-testid={`orientation-exam-${s.id}`}>
                    {(() => {
                      const info = examInfo[s.id];
                      if (!info) return <p className="text-sm text-stone-400">Loading exam…</p>;
                      if (s.done_at || info.passed) {
                        return (
                          <span className="inline-flex items-center gap-2 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/5 px-4 py-2 text-sm font-medium text-[#1e8449]" data-testid={`orientation-exam-passed-${s.id}`}>
                            <CheckCircle size={18} weight="fill" /> Exam passed{info.score != null ? ` — ${info.score}%` : ""}{s.done_at ? ` · ${String(s.done_at).slice(0, 10)}` : ""}
                          </span>
                        );
                      }
                      if (isAdmin) return <p className="text-sm text-stone-400">{info.configured ? `${(info.questions || []).length} questions · passing ${info.passing_score}% (staff view)` : "Questions not configured yet."}</p>;
                      if (!info.configured) return <p className="text-sm text-stone-500">The office is preparing the exam questions — check back soon.</p>;
                      if (info.locked) return <p className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-4 text-sm font-medium text-[#C0392B]" data-testid={`orientation-exam-locked-${s.id}`}>Attempt limit reached — the office has been notified and will help you reset.</p>;
                      if (!info.videos_complete) return <p className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-500" data-testid={`orientation-exam-waiting-${s.id}`}>The exam unlocks once all video training topics above are signed.</p>;
                      if (info.remediations_active) return <p className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4 text-sm text-[#8a5d14]">Finish the assigned rewatch above before retaking the exam.</p>;
                      const answers = examAnswers[s.id] || {};
                      const res = examResult?.sid === s.id ? examResult : null;
                      return (
                        <div className="rounded-2xl border border-[#82778A]/30 bg-white p-5" data-testid={`orientation-exam-form-${s.id}`}>
                          <p className="flex items-center gap-2 text-sm font-semibold text-stone-900"><GraduationCap size={18} className="text-primary" /> {info.questions.length} questions · passing score {info.passing_score}%{info.attempts_left != null ? ` · ${info.attempts_left} attempt(s) remaining` : ""}</p>
                          {res && !res.passed && (
                            <p className="mt-3 rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-3 text-sm font-medium text-[#C0392B]" data-testid={`orientation-exam-failed-${s.id}`}>
                              Score {res.score}% — below the {res.passing_score}% needed. Review the parts above and try again{res.attempts_left != null ? ` (${res.attempts_left} attempt(s) left)` : ""}.
                            </p>
                          )}
                          <div className="mt-4 space-y-5">
                            {info.questions.map((q, qi) => (
                              <div key={q.id} data-testid={`exam-question-${q.id}`}>
                                <p className="text-sm font-medium text-stone-800">{qi + 1}. {q.text}</p>
                                <div className="mt-2 space-y-1.5">
                                  {q.choices.map((c, ci) => (
                                    <label key={ci} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${answers[q.id] === ci ? "border-primary bg-primary/5 text-stone-900" : "border-stone-200 text-stone-600 hover:border-stone-300"}`} data-testid={`exam-choice-${q.id}-${ci}`}>
                                      <input type="radio" name={`exam-${s.id}-${q.id}`} className="mt-0.5" checked={answers[q.id] === ci}
                                        onChange={() => setExamAnswers((a) => ({ ...a, [s.id]: { ...(a[s.id] || {}), [q.id]: ci } }))} />
                                      <span>{c}</span>
                                    </label>
                                  ))}
                                </div>
                                {res?.review?.find((r) => r.id === q.id) && (
                                  <p className="mt-1.5 text-xs text-stone-500">Correct answer: {q.choices[res.review.find((r) => r.id === q.id).correct_index]}{res.review.find((r) => r.id === q.id).explanation ? ` — ${res.review.find((r) => r.id === q.id).explanation}` : ""}</p>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="mt-5 border-t border-stone-100 pt-4">
                            <p className="mb-2 text-sm font-medium text-stone-800">Signature — I attest these answers are my own work</p>
                            <SignaturePad onChange={setExamSig} />
                          </div>
                          <div className="mt-4 flex justify-end">
                            <Button onClick={() => submitExam(s)} disabled={busy} className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid={`orientation-exam-submit-${s.id}`}>
                              {busy ? "Submitting…" : "Submit exam"}
                            </Button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : s.seed_key === "designated-caregiver-step" ? (
                <div className="mt-6 clear-both max-w-2xl" data-testid={`orientation-dc-${s.id}`}>
                  {s.done_at ? (
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/5 px-4 py-2 text-sm font-medium text-[#1e8449]" data-testid={`orientation-done-${s.id}`}>
                      <CheckCircle size={18} weight="fill" /> Answered & signed {String(s.done_at).slice(0, 10)}
                    </span>
                  ) : isAdmin ? (
                    <p className="text-sm text-stone-400">Staff answer and sign the designated caregiver question here (staff view).</p>
                  ) : dcFile ? (
                    <DesignatedCaregiverCard uid={user.id} emp={dcFile} startOpen
                      onSaved={() => { loadDcFile(); load(); }} />
                  ) : (
                    <p className="text-sm text-stone-400" data-testid={`orientation-dc-loading-${s.id}`}>{dcFile === false ? "Couldn't load your employee record — the office can answer this with you." : "Loading…"}</p>
                  )}
                </div>
                ) : (
                <div className="mt-6 flex items-center gap-3 clear-both">
                  {s.done_at ? (
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/5 px-4 py-2 text-sm font-medium text-[#1e8449]" data-testid={`orientation-done-${s.id}`}>
                      <CheckCircle size={18} weight="fill" /> Completed {String(s.done_at).slice(0, 10)}
                    </span>
                  ) : (
                    <Button onClick={() => (s.screening_form_id ? (setFormStep(s), setFormResp({}), setFormSig(null)) : markDone(s))}
                      disabled={busy}
                      className="rounded-full bg-primary px-5 text-primary-foreground hover:bg-stone-900"
                      data-testid={`orientation-read-${s.id}`}>
                      {s.screening_form_id ? "Fill & sign form" : "I have read and understood this part"}
                    </Button>
                  )}
                </div>
                )}
              </section>
            );
          })}

          {ackStep && (
            <section id="sec-ack" ref={(el) => { secRefs.current["sec-ack"] = el; }}
              className={`scroll-mt-24 rounded-2xl border p-6 sm:p-8 ${data.acknowledged ? "border-[#27AE60]/40 bg-[#27AE60]/5" : allDone ? "border-[#82778A]/40 bg-[#F2EFEA]" : "border-stone-200 bg-stone-50"}`}
              data-testid="orientation-ack-card">
              <div className="flex items-center gap-3">
                {data.acknowledged ? <CheckCircle size={24} weight="fill" className="text-[#27AE60]" /> : allDone ? <Signature size={24} className="text-[#82778A]" /> : <LockSimple size={22} className="text-stone-400" />}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#82778A]">Final step</p>
                  <h2 className="mt-0.5 font-heading text-2xl font-semibold tracking-tight text-stone-900">{ackStep.title}</h2>
                  <p className="mt-1 text-sm text-stone-500">
                    {data.acknowledged
                      ? `Signed ${String(data.ack?.signed_at).slice(0, 10)} — filed on your record`
                      : allDone
                        ? "All parts read — review the acknowledgment below and sign to finish orientation."
                        : "Unlocks after all orientation parts are checked off."}
                  </p>
                </div>
                {isAdmin && (
                  <button onClick={() => openEdit(ackStep)} className="rounded-full p-1.5 text-stone-300 hover:bg-white hover:text-stone-700" data-testid={`orientation-edit-${ackStep.id}`}><PencilSimple size={16} /></button>
                )}
              </div>
              {(allDone || data.acknowledged) && (
                <div className="mt-5">
                  <div className="max-h-64 overflow-y-auto rounded-xl border border-stone-200 bg-white p-5" data-testid="orientation-ack-content">
                    <OrientationBody text={ackStep.content} small />
                  </div>
                  {!data.acknowledged && (
                    <div className="mt-5 space-y-3">
                      <SignaturePad onChange={setSig} />
                      <div className="flex justify-end">
                        <Button onClick={signAck} disabled={busy} className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="orientation-sign-submit">
                          {busy ? "Signing..." : "Sign & Complete Orientation"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {refs.length > 0 && (
            <section id="sec-refs" ref={(el) => { secRefs.current["sec-refs"] = el; }} className="scroll-mt-24" data-testid="orientation-references">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#82778A]">Always available</p>
              <h2 className="mt-1.5 font-heading text-2xl font-semibold tracking-tight text-stone-900">Reference library</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <a href="/handbook" className="flex items-center justify-between gap-2 rounded-2xl border-2 border-primary/30 bg-primary/5 px-5 py-4 transition-shadow hover:shadow-sm" data-testid="orientation-handbook-link">
                  <div className="flex min-w-0 items-center gap-3">
                    <BookOpen size={18} className="shrink-0 text-primary" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-stone-900">Employee Handbook (full text)</p>
                      <p className="text-[11px] text-stone-500">Every policy &amp; protection — opens in the app, with Listen</p>
                    </div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-primary">Open →</span>
                </a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/my/forms/lst-exemption-certificate.pdf`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-between gap-2 rounded-2xl border border-stone-200 bg-white px-5 py-4 transition-shadow hover:shadow-sm"
                  data-testid="orientation-lst-exemption-link">
                  <div className="flex min-w-0 items-center gap-3">
                    <BookOpen size={18} className="shrink-0 text-[#82778A]" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-stone-900">Local Services Tax — Exemption Certificate</p>
                      <p className="text-[11px] text-stone-500">Optional — only if you qualify and want to claim exempt from the local services tax</p>
                    </div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-primary">Open →</span>
                </a>
                {refs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 rounded-2xl border border-stone-200 bg-white px-5 py-4 transition-shadow hover:shadow-sm" data-testid={`orientation-reference-${s.id}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <BookOpen size={18} className="shrink-0 text-[#82778A]" />
                      <p className="min-w-0 truncate text-sm font-medium text-stone-800">{s.title}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button onClick={() => setReading(s)} className="text-xs font-semibold text-primary hover:underline" data-testid={`orientation-ref-read-${s.id}`}>Open</button>
                      {isAdmin && (
                        <>
                          <button onClick={() => openEdit(s)} className="text-stone-300 hover:text-stone-700" data-testid={`orientation-edit-${s.id}`}><PencilSimple size={15} /></button>
                          <button onClick={() => removeStep(s)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`orientation-delete-${s.id}`}><Trash size={15} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>

      {formStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="orientation-form-modal">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">{formStep.title}</h2>
            <div className="mt-1"><OrientationBody text={formStep.content} small /></div>
            {(() => {
              const fd = forms.find((x) => x.id === formStep.screening_form_id);
              if (!fd) return <p className="mt-4 text-sm text-stone-400">Loading form…</p>;
              return (
                <div className="mt-4 space-y-4" data-testid="orientation-form-fields">
                  {(fd.fields || []).map((f) => (
                    <div key={f.id}>
                      <p className="text-sm font-medium text-stone-800">{f.label}</p>
                      {f.type === "checkboxes" ? (
                        <div className="mt-2 space-y-1.5">
                          {f.options.map((opt) => (
                            <label key={opt} className="flex items-start gap-2 text-sm text-stone-600" data-testid={`orientation-form-opt-${f.id}-${opt.slice(0, 12).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                              <input type="checkbox" className="mt-0.5" checked={(formResp[f.id] || []).includes(opt)} onChange={() => toggleResp(f.id, opt)} />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      ) : f.type === "date" ? (
                        <Input type="date" value={formResp[f.id] || ""} onChange={(e) => setFormResp((r) => ({ ...r, [f.id]: e.target.value }))} className="mt-1 h-9 w-48 border-stone-300 bg-white text-sm" data-testid={`orientation-form-date-${f.id}`} />
                      ) : (
                        <Input value={formResp[f.id] || ""} onChange={(e) => setFormResp((r) => ({ ...r, [f.id]: e.target.value }))} className="mt-1 h-9 border-stone-300 bg-white text-sm" data-testid={`orientation-form-text-${f.id}`} />
                      )}
                    </div>
                  ))}
                  <div>
                    <p className="mb-1 text-sm font-medium text-stone-800">Signature</p>
                    <SignaturePad onChange={setFormSig} />
                  </div>
                </div>
              );
            })()}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFormStep(null)} className="border-stone-300 text-stone-500" data-testid="orientation-form-cancel">Cancel</Button>
              <Button onClick={submitFormStep} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="orientation-form-submit">
                {busy ? "Submitting…" : "Sign & submit"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="orientation-read-modal">
          <div className="max-h-[85vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">{reading.title}</h2>
            <p className="mt-0.5 text-xs text-stone-400">{KIND_LABELS[reading.kind]} · v{reading.version}</p>
            <div className={videoEmbed(reading.video_url) ? "mt-3 grid gap-4 md:grid-cols-2" : "mt-3"}>
              {videoEmbed(reading.video_url) && (
                <div className="overflow-hidden rounded-xl border border-stone-200" data-testid="orientation-read-video">
                  {videoEmbed(reading.video_url).type === "iframe" ? (
                    <div className="aspect-video w-full"><iframe src={videoEmbed(reading.video_url).src} title="video" className="h-full w-full" allowFullScreen /></div>
                  ) : (
                    <video src={videoEmbed(reading.video_url).src} controls className="max-h-[46vh] w-full bg-stone-900 object-contain" />
                  )}
                </div>
              )}
              <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="orientation-read-content">
                <OrientationBody text={reading.content} small />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReading(null)} className="border-stone-300 text-stone-500" data-testid="orientation-read-close">Close</Button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="orientation-edit-modal">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">{editing.id ? "Edit step" : "Add step"}</h2>
            {editing.id && <p className="mt-1 text-xs text-[#B7791F]">Changing the content bumps the version number.</p>}
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Title</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="border-stone-300 bg-white" data-testid="orientation-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Order</Label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                    className="border-stone-300 bg-white" data-testid="orientation-order-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="orientation-kind-select">
                  <option value="curriculum">Orientation part (requires check-off)</option>
                  <option value="reference">Reference (always available, no check-off)</option>
                  <option value="acknowledgment">Final acknowledgment (signature step)</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Video URL (optional — YouTube, Vimeo, or direct video link)</Label>
                <Input value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
                  placeholder="https://vimeo.com/…" className="border-stone-300 bg-white" data-testid="orientation-video-input" />
                </div>
              <div className="space-y-1.5">
                <Label>Attached form (optional)</Label>
                <select value={editing.screening_form_id || ""} onChange={(e) => setEditing({ ...editing, screening_form_id: e.target.value })}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="orientation-form-select">
                  <option value="">None — reading step</option>
                  {forms.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                </select>
                <p className="text-xs text-stone-400">When attached, staff fill &amp; sign this form to complete the step.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Content</Label>
                <Textarea rows={12} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  className="border-stone-300 bg-white text-sm leading-relaxed" data-testid="orientation-content-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-stone-300 text-stone-500" data-testid="orientation-edit-cancel">Cancel</Button>
                <Button onClick={saveStep} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="orientation-step-save">
                  {busy ? "Saving..." : "Save Step"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
